import { Box } from '@mui/material';
import DraggableOptionEntry from './DraggableOptionEntry';
import { SurveyV2 } from '@act/shared/data-transfer-objects';

interface OptionsCreatorProps {
  questionKey: string;
  options: SurveyV2.CreateOptionJSONDto[];
  questionCount: number;
  isActionSurvey: boolean;
  onOptionsChange: (options: SurveyV2.CreateOptionJSONDto[]) => void;
  formErrors?: any; // Form validation errors from React Hook Form
  questionIndex?: number; // Index of this question in the questions array
}

const OptionCreator = ({
  questionKey,
  options,
  questionCount,
  isActionSurvey,
  onOptionsChange,
  formErrors,
  questionIndex,
}: OptionsCreatorProps) => {
  function handleUpdateOption(
    updatedOption: SurveyV2.CreateOptionJSONDto
  ): void {
    const updatedOptions = [...options].map(option => {
      if (option.sortOrder === updatedOption.sortOrder) {
        return {
          ...updatedOption,
          text: updatedOption.text || '',
        };
      }
      return option;
    });
    onOptionsChange(updatedOptions);
  }

  function handleAddOption(sortOrder: number): void {
    const newOptions = [...options];
    const insertIndex =
      options.findIndex(opt => opt.sortOrder === sortOrder) + 1;

    newOptions.splice(insertIndex, 0, {
      ...SurveyV2.DEFAULT_OPTION,
      sortOrder: sortOrder + 1,
      text: '',
    });

    const finalOptions = newOptions.map((option, index) => ({
      ...option,
      sortOrder: index + 1,
    }));

    onOptionsChange(finalOptions);
  }

  function handleRemoveOption(sortOrder: number): void {
    if (options.length <= 1) {
      onOptionsChange([{ ...SurveyV2.DEFAULT_OPTION, text: '' }]);
    } else {
      const updatedOptions = options
        .filter(option => option.sortOrder !== sortOrder)
        .map((option, index) => ({
          ...option,
          sortOrder: index + 1,
        }));
      onOptionsChange(updatedOptions);
    }
  }

  const handleMoveOption = (dragIndex: number, hoverIndex: number) => {
    const dragOption = options[dragIndex];
    const newOptions = [...options];
    newOptions.splice(dragIndex, 1);
    newOptions.splice(hoverIndex, 0, dragOption);

    const updatedOptions = newOptions.map((option, index) => ({
      ...option,
      sortOrder: index + 1,
    }));

    onOptionsChange(updatedOptions);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        padding: '12px 0',
        boxSizing: 'border-box',
      }}
    >
      {options.map((option, index) => (
        <DraggableOptionEntry
          id={`${questionKey}-option-${option.sortOrder}`}
          key={`${questionKey}-option-entry-${option.sortOrder}`}
          index={index}
          questionKey={questionKey}
          option={option}
          questionCount={questionCount}
          isActionSurvey={isActionSurvey}
          handleAddOption={handleAddOption}
          handleMoveOption={handleMoveOption}
          handleRemoveOption={handleRemoveOption}
          handleUpdateOption={handleUpdateOption}
          formErrors={formErrors}
          questionIndex={questionIndex}
          optionIndex={index}
        />
      ))}
    </Box>
  );
};

export default OptionCreator;
